import React from 'react';
import styled from 'react-emotion';
import {
  Tabs,
  Tab,
  Badge,
  Area,
  Loader,
  Box,
  Typography,
  IconButton,
  Tooltip,
  AULA_NOTIFICATION_HOVER_BG_COLOUR,
  AULA_NOTIFICATION_HOVER_BORDER_COLOUR,
  tooltipClasses,
  Link,
} from '@ublend-npm/aulaui-core';
import { DARK } from '@core/constants/theme';

// Main container styles
export const StyledNotificationsContainer = styled(Box)`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  background-color: ${({ theme }) =>
    theme.palette.mode === DARK
      ? theme.palette.background.paper
      : theme.palette.background.paper};

  @media (max-width: 768px) {
    margin: 0;
  }
`;

export const StyledHeaderContainer = styled(Box)`
  padding: 20px 16px 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const StyledHeaderLeft = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const StyledHeaderRight = styled(Box)`
  display: flex;
  gap: 8px;
`;

export const StyledNotificationsTitle = styled(Typography)`
  && {
    font-weight: 600 !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    font-size: 1.25rem !important;
    line-height: 1.4 !important;
  }
`;

// Filter/Tabs section styles
export const StyledFilterContainer = styled(Box)`
  display: flex;
  padding-left: 16px;
  padding-right: 16px;
  gap: 8px;
  flex-wrap: wrap;
  overflow: hidden;
`;

export const StyledTabs = styled(Tabs)`
  && {
    min-height: 42px !important;
  }

  && .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.palette.primary.main} !important;
    height: 3px !important;
  }

  && .MuiTabs-flexContainer {
    gap: 4px !important;
  }

  && .MuiTabs-scroller {
    overflow-x: auto !important;
  }
`;

export const StyledTab = styled(Tab)`
  && {
    font-weight: 600 !important;
    max-width: ${({ value }) => {
      switch (value) {
        case 'All':
          return '49px';
        case 'Posts':
          return '68px';
        case 'Comments':
          return '101px';
        default:
          return 'auto';
      }
    }} !important;
    min-width: ${({ value }) => {
      switch (value) {
        case 'All':
          return '49px';
        case 'Posts':
          return '68px';
        case 'Comments':
          return '101px';
        default:
          return 'auto';
      }
    }} !important;
    height: 42px !important;
    padding: 6px 16px !important;
    text-transform: capitalize !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.text.secondary
        : theme.palette.text.secondary} !important;
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main} !important;
    font-weight: 600 !important;
  }

  &:hover {
    background-color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.action.hover
        : theme.palette.action.hover} !important;
  }
`;

// Badge styles
export const StyledBadge = styled(Badge)`
  && {
    padding-left: 8px !important;
  }

  && .MuiBadge-badge {
    background-color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.black
        : AULA_NOTIFICATION_HOVER_BG_COLOUR} !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.common.black} !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    min-width: 20px !important;
    height: 20px !important;
    border-radius: 10px !important;
  }
`;

// Content area styles
export const StyledContentArea = styled(Box)`
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
  height: 68vh;
  @media screen and (max-width: ${({ theme }) =>
      theme.breakpoints.values.sm}px) {
    height: auto;
    max-height: 400px;
  }
`;

export const StyledLoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`;

export const StyledLoader = styled(Loader)`
  color: ${({ theme }) => theme.palette.primary.main};
`;

export const StyledEmptyStateContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  margin: 8px;
  text-align: center;
  background-color: ${({ theme }) =>
    theme.palette.mode === DARK
      ? theme.palette.background.default
      : theme.palette.background.default};
`;

export const StyledEmptyStateText = styled(Typography)`
  && {
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    margin-bottom: 4px !important;
    font-size: 1rem !important;
    font-weight: 500 !important;
    text-align: center !important;
  }
`;

export const StyledEmptyStateText2 = styled(Typography)`
  && {
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.text.secondary
        : theme.palette.text.secondary} !important;
    margin-bottom: 8px !important;
    font-size: 0.875rem !important;
    line-height: 1.4 !important;
    text-align: center !important;
    font-weight: 400 !important;
  }
`;

export const StyledEmptyStateLink = styled(Link)`
  font-size: 14px !important;
`;

// Notification item styles
export const StyledNotificationItem = styled(Area)`
  && {
    cursor: pointer !important;
    position: relative !important;
    padding: 8px !important;
    transition: all 0.2s ease-in-out !important;
    border-radius: 4px !important;
    background-color: ${({ theme, isUnread }) => {
      if (isUnread) {
        return theme.palette.mode === DARK
          ? theme.palette.background.default
          : theme.palette.background.default;
      }
      return 'transparent';
    }} !important;
  }

  &&:hover {
    background-color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.black
        : AULA_NOTIFICATION_HOVER_BG_COLOUR} !important;
  }

  &&:focus {
    border: 2px solid ${AULA_NOTIFICATION_HOVER_BORDER_COLOUR} !important;
    outline: none !important;
    background-color: ${({ theme, isUnread }) => {
      if (isUnread) {
        return theme.palette.mode === DARK
          ? theme.palette.background.default
          : theme.palette.background.default;
      }
      return theme.palette.background.default;
    }} !important;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    &&:hover {
      background-color: ${({ theme, isUnread }) => {
        if (isUnread) {
          return theme.palette.mode === DARK
            ? theme.palette.background.default
            : theme.palette.background.default;
        }
        return 'transparent';
      }} !important;
    }
  }

  &&:last-of-type {
    margin-bottom: calc(50px - 4vh);
    @media screen and (max-width: ${({ theme }) =>
        theme.breakpoints.values.sm}px) {
      margin-bottom: calc(100px - 8vh);
    }
  }
`;

export const StyledUnreadIndicator = styled(Box)`
  position: absolute;
  top: 16px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary.main};
  cursor: pointer;
`;

export const StyledNotificationContent = styled(Box)`
  display: flex;
  gap: 16px;
`;

export const StyledNotificationDetails = styled(Box)`
  flex: 1;
  min-width: 0;
`;

export const StyledNotificationHeader = styled(Typography)`
  && {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
    font-size: 0.875rem;
    line-height: 1.4;
    max-width: calc(100% - 12px);
  }
`;

export const StyledUserName = styled(Typography)`
  && {
    font-weight: 600 !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    font-size: 16px !important;
    line-height: 1.4 !important;
  }
`;

export const StyledActionText = styled(Typography)`
  && {
    font-size: 0.875rem !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    font-weight: 400 !important;
    line-height: 1.4 !important;
  }
`;

export const StyledSpaceText = styled(Typography)`
  && {
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    line-height: 1.4 !important;
  }
`;

export const StyledEducatorChip = styled.div`
  padding: 2px 8px !important;
  border-radius: 12px !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  background-color: ${({ theme }) => theme.palette.secondary.main} !important;
  color: ${({ theme }) =>
    theme.palette.mode === DARK
      ? theme.palette.common.black
      : theme.palette.common.white} !important;
  white-space: nowrap !important;
  display: inline-block !important;
  line-height: 1.2 !important;
  text-transform: capitalize !important;
  letter-spacing: 0.5px !important;
`;

export const StyledMetaInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const StyledTimeAgo = styled(Typography)`
  && {
    font-size: 0.75rem;
    margin-bottom: 4px;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.text.secondary
        : theme.palette.text.secondary};
    font-weight: 400;
    line-height: 1.2;
  }
`;

export const StyledContentPreview = styled(Typography)`
  && {
    display: -webkit-box !important;
    -webkit-line-clamp: 1 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    margin-bottom: 4px !important;
    line-height: 1.8 !important;
    font-size: 0.875rem !important;
    padding: 8px !important;
    border-radius: 4px !important;
    height: 36px !important;
    padding-top: 6px !important;
    word-break: break-word !important;
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    background-color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.background.paper
        : theme.palette.background.paper} !important;
    font-weight: 400 !important;
  }
`;

// Action button styles
export const StyledActionButton = styled(IconButton)`
  && {
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.common.white
        : theme.palette.text.primary} !important;
    padding: 8px !important;
    border-radius: 4px !important;
    transition: all 0.2s ease-in-out !important;
  }

  &&:hover {
    background-color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.action.hover
        : theme.palette.action.hover} !important;
    transform: scale(1.05) !important;
  }

  &&.Mui-disabled {
    color: ${({ theme }) =>
      theme.palette.mode === DARK
        ? theme.palette.action.disabled
        : theme.palette.action.disabled} !important;

    .MuiSvgIcon-root {
      fill: ${({ theme }) =>
        theme.palette.mode === DARK
          ? theme.palette.action.disabled
          : theme.palette.action.disabled} !important;
    }
  }
`;

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color:
      theme.palette.mode === DARK
        ? theme.palette.primary.main
        : theme.palette.grey['700'],
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === DARK
        ? theme.palette.primary.main
        : theme.palette.grey['700'],
    color:
      theme.palette.mode === DARK
        ? theme.palette.common.black
        : theme.palette.common.white,
  },
}));
